import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";
import { adaptStoreReport } from "src/features/reporting/adapters";
import StoreObservationTableCell from "../../components/StoreObservationTableCell/StoreObservationTableCell";
import IndividualObservationTableCell from "../../components/IndividualObservationTableCell/IndividualObservationTableCell";
import { UseFormSetValue } from "react-hook-form";
import GreatConversationTableCell from "../../components/GreatConversationTableCell/GreatConversationTableCell";

interface StoreReportOptions {
	year: number;
	form: number;
	areaId: number;
	compliancy?: boolean;
	threshold?: boolean;
	observationType: IObservationType;
	setValue: UseFormSetValue<ReportDetailForm>;
}

const columnHelper = createColumnHelper<any>();

const requestStoreReportExport = async (
	year: number,
	form: number,
	area: number,
	type: "complete" | "compliant" | "threshold"
) => {
	await cmsClient.post("/observations/reports/store-by-month/", {
		form_id: form,
		area_id: area,
		year,
		include_ignored: true,
		type
	});
};

const fetchStoreReport = async (year: number, form: number, area: number) => {
	const response = await cmsClient.get<IStoreReport[]>(
		"/observations/reports/store-by-month/",
		{
			params: {
				year,
				form,
				area,
				includeIgnored: true
			}
		}
	);

	return response.data;
};

const useStoreReport = ({
	year,
	form,
	areaId,
	compliancy,
	threshold,
	observationType,
	setValue
}: StoreReportOptions) => {
	const queryKey = ["store-report", { year, form, areaId }];
	const queryFn = () => fetchStoreReport(year, form, areaId);
	const select = (list: IStoreReport[]) => adaptStoreReport(list);

	const query = useQuery({ queryKey, queryFn, select });

	const months = Array.from({ length: 12 }, (_, i) => i).map((month) =>
		dayjs().month(month).format("MMM")
	);

	const monthlyColumns = months.map((month) => {
		return columnHelper.accessor(month, {
			cell: ({ cell, row }) => {
				if (observationType === "INDIVIDUAL") {
					return <IndividualObservationTableCell cellValue={cell.getValue()} />;
				}

				if (observationType === "GREAT_CONVERSATION") {
					return <GreatConversationTableCell cellValue={cell.getValue()} />;
				}

				return (
					<StoreObservationTableCell
						cellValue={cell.getValue()}
						showStatistics={false}
						storeId={row.original.storeId}
					/>
				);
			},
			header: month
		});
	});

	const columns = [
		columnHelper.display({
			id: "store-name",
			cell: ({ row }) => row.original.storeName,
			header: "Store Name"
		}),
		...monthlyColumns
	];

	const download = async () => {
		try {
			await requestStoreReportExport(
				year,
				form,
				areaId,
				compliancy ? "compliant" : threshold ? "threshold" : "complete"
			);
			toast.success(
				<CustomToast
					type="Success"
					message="A link to your export (Store by month) has been generated. Check your mail inbox for the link."
				/>,
				{ hideProgressBar: true }
			);
		} catch (error) {
			toast.error(
				<CustomToast type="Error" message="Something went wrong, try again" />,
				{ hideProgressBar: true }
			);
		}
	};

	return { columns, download, ...query };
};

export default useStoreReport;
